import { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Avatar,
  useMediaQuery,
  styled,
  useTheme,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
} from "@mui/material";
import { ArrowBack, ArrowForward, Close } from "@mui/icons-material";
import apiClient from "../utils/apiClient";

const GradientText = styled(Typography)(({ theme }) => ({
  background: "black",
  color: "black",
  fontWeight: 500,
  marginBottom: "40px",
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
  fontFamily: "Aclonica",
  textAlign: "left",
  display: "inline-block",
}));

const GradientButton = styled(Button)(({ theme }) => ({
  background: "linear-gradient(to right, #4ECDC4, #2196F3)",
  color: theme.palette.common.white,
  borderRadius: 30,
  padding: theme.spacing(1.5, 4),
  fontSize: "1rem",
  fontWeight: 600,
  textTransform: "none",
  boxShadow: "0 4px 14px rgba(33, 150, 243, 0.4)",
  transition: "all 0.3s ease",
  "&:hover": {
    background: "linear-gradient(to right, #4ECDC4, #2196F3)",
    boxShadow: "0 6px 20px rgba(33, 150, 243, 0.6)",
    transform: "translateY(-2px)",
  },
}));

const FeedbackSection2 = () => {
  const theme = useTheme();
  const [latestFeedbacks, setLatestFeedbacks] = useState([]);
  const [expandedIndex, setExpandedIndex] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [tenLatestFeedbacks, setTenLatestFeedbacks] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    const fetchFeedbacks = async () => {
      try {
        const response = await apiClient.get(`/feedbacks/latest`);
        setLatestFeedbacks(response.data.data);
      } catch (error) {
        console.error("Error fetching feedbacks:", error);
      }
    };
    fetchFeedbacks();
  }, []);

  const handleClick = async () => {
    try {
      const response = await apiClient.get(`/feedbacks/ten-latest`);
      setTenLatestFeedbacks(response.data.data);
      setCurrentSlide(0); // reset to first slide when opening
      setOpenDialog(true);
    } catch (error) {
      console.error("Error fetching feedbacks:", error);
    }
  };

  const toggleCard = (index) => {
    setExpandedIndex(expandedIndex === index ? null : index);
  };

  const handleNext = () => {
    setCurrentSlide((prev) => (prev + 1) % tenLatestFeedbacks.length);
  };

  const handlePrev = () => {
    setCurrentSlide(
      (prev) =>
        (prev - 1 + tenLatestFeedbacks.length) % tenLatestFeedbacks.length
    );
  };

  return latestFeedbacks.length > 0 ? (
    <Box sx={{ width: "80%", mx: "auto", mt: 10 }}>
      <GradientText variant={isSmallScreen ? "h4" : "h3"} fontWeight={700}>
        Aptitude Test Feedbacks
      </GradientText>

      <Grid container spacing={4}>
        {latestFeedbacks.map((feedback, index) => {
          const isExpanded = expandedIndex === index;
          return (
            <Grid item xs={12} sm={6} key={feedback.feedback_id || index}>
              <Card
                sx={{
                  height: isExpanded ? "auto" : 150,
                  overflow: "hidden",
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  boxShadow: isExpanded ? 6 : 2,
                  borderRadius: 3,
                }}
                onClick={() => toggleCard(index)}
              >
                <Box sx={{ display: "flex", alignItems: "center", p: 2 }}>
                  <Avatar
                    src={
                      feedback.user_image ||
                      `https://ui-avatars.com/api/?name=${feedback.username}&background=4ECDC4&color=fff`
                    }
                    alt={feedback.username}
                    sx={{ width: 70, height: 70, mr: 2 }}
                  />
                  <Box>
                    <Typography variant="h6" sx={{ fontFamily: "Aclonica" }}>
                      {feedback.username}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {new Date(feedback.feedback_timestamp).toLocaleDateString(
                        "en-GB",
                        {
                          day: "numeric",
                          month: "long",
                          year: "numeric",
                        }
                      )}
                    </Typography>
                  </Box>
                </Box>

                <CardContent sx={{ pt: 0 }}>
                  <Typography
                    variant="body1"
                    sx={{
                      color: "#555",
                      transition: "all 0.3s ease",
                      display: "-webkit-box",
                      WebkitLineClamp: isExpanded ? "none" : 3,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {feedback.feedback}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      <Box sx={{ textAlign: "center", mt: 4 }}>
        <GradientButton onClick={handleClick}>
          See more feedbacks
        </GradientButton>
      </Box>

      {/* Dialog for slideshow */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          More Feedbacks
          <IconButton onClick={() => setOpenDialog(false)}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {tenLatestFeedbacks.length > 0 && (
            <Box sx={{ textAlign: "center", px: 2 }}>
              <Avatar
                src={
                  tenLatestFeedbacks[currentSlide].user_image ||
                  `https://ui-avatars.com/api/?name=${tenLatestFeedbacks[currentSlide].username}&background=4ECDC4&color=fff`
                }
                alt={tenLatestFeedbacks[currentSlide].username}
                sx={{ width: 80, height: 80, mx: "auto", mb: 2 }}
              />
              <Typography variant="h6" sx={{ fontFamily: "Aclonica" }}>
                {tenLatestFeedbacks[currentSlide].username}
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={2}>
                {new Date(
                  tenLatestFeedbacks[currentSlide].feedback_timestamp
                ).toLocaleDateString("en-GB", {
                  day: "numeric",
                  month: "long",
                  year: "numeric",
                })}
              </Typography>
              <Typography variant="body1" sx={{ mb: 2, textAlign: "left" }}>
                {tenLatestFeedbacks[currentSlide].feedback}
              </Typography>
              <Box sx={{ display: "flex", justifyContent: "center", gap: 2 }}>
                <IconButton onClick={handlePrev}>
                  <ArrowBack />
                </IconButton>
                <IconButton onClick={handleNext}>
                  <ArrowForward />
                </IconButton>
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  ) : null;
};

export default FeedbackSection2;
